import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  ActionService,
  ACTION_STATUS_LABELS,
  ACTION_PRIORITY_LABELS,
  type EnterpriseAction,
  type ActionStatus,
} from '../../services/actionService';
import { IssueService } from '../../services/issueService';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import { resolveIssueAssigneeName } from '../../utils/issueAssignee';
import { actionCompletionPct } from '../../utils/issueProgress';

interface ProblemActionDrawerProps {
  actionId: string | null;
  organizationId: string;
  issueId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const ProblemActionDrawer: React.FC<ProblemActionDrawerProps> = ({
  actionId,
  organizationId,
  issueId,
  onClose,
  onUpdated,
}) => {
  const { members } = useOrgMembers(organizationId);
  const [action, setAction] = useState<EnterpriseAction | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!actionId) { setAction(null); return; }
    setLoading(true);
    ActionService.getById(actionId).then(a => { setAction(a); setLoading(false); });
  }, [actionId]);

  if (!actionId) return null;

  const handleStatusChange = async (status: ActionStatus) => {
    if (!action) return;
    setSaving(true);
    const updated = await ActionService.update(action.id, { status });
    if (updated) {
      if (status === 'completed') {
        await IssueService.appendHistory(organizationId, issueId, `Ação concluída: ${action.title}`, 'action_linked');
        await IssueService.syncLinkedActionsCount(organizationId, issueId);
      }
      setAction(updated);
      onUpdated();
    }
    setSaving(false);
  };

  const pct = action ? actionCompletionPct(action) : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-[380px] flex-col border-l border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
          <span className="text-[11px] font-semibold uppercase text-slate-500">Detalhe da Ação</span>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading && <p className="text-[11px] text-slate-400">Carregando...</p>}
          {action && (
            <div className="space-y-3">
              <h3 className="text-[13px] font-semibold text-slate-900 dark:text-slate-100">{action.title}</h3>
              {action.description && (
                <p className="text-[11px] text-slate-600 dark:text-slate-400">{action.description}</p>
              )}
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div><span className="text-slate-400">Status</span><p className="font-medium">{ACTION_STATUS_LABELS[action.status]}</p></div>
                <div><span className="text-slate-400">Prioridade</span><p className="font-medium">{ACTION_PRIORITY_LABELS[action.priority]}</p></div>
                <div><span className="text-slate-400">Responsável</span><p className="font-medium">{resolveIssueAssigneeName(action.assigned_to, members)}</p></div>
                <div><span className="text-slate-400">Prazo</span><p className="font-medium">{action.due_date ? new Date(action.due_date).toLocaleDateString('pt-BR') : '—'}</p></div>
              </div>
              <div>
                <div className="mb-1 flex justify-between text-[10px]"><span className="text-slate-400">Conclusão</span><span>{pct}%</span></div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
              {action.status !== 'completed' && action.status !== 'cancelled' && (
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {(['pending', 'in_progress', 'completed', 'blocked'] as ActionStatus[]).map(s => (
                    <button
                      key={s}
                      type="button"
                      disabled={saving || action.status === s}
                      onClick={() => handleStatusChange(s)}
                      className={`rounded px-2 py-1 text-[10px] font-medium ${action.status === s ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50 dark:border-slate-600'}`}
                    >
                      {ACTION_STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};
